// The engine's functions for people typing formulas (design 02 5.1, 9): a
// catalog by category with each one's arguments in Korean and what it
// does, the call the caret is in and which of its arguments is being typed
// (the guide over the formula bar), and the name being typed (suggestions).
// funcs.test.ts holds the catalog to the engine's tables (server/src/calc):
// every function there is here, with as many arguments.
//
// A signature is the arguments in order: "[…]" optional, "…" the group
// before it again, after "…" the last ones (LET's calculation), and
// "over=차원" the named arguments (design 02 6.1).

export type GroupId =
  | 'sum'
  | 'cond'
  | 'math'
  | 'look'
  | 'date'
  | 'text'
  | 'money'
  | 'array'
  | 'info'
  | 'pred'
  | 'matrix'
  | 'tensor'
  | 'lambda'

/** The picker's tabs: the often used ones, every category, and the workbook's names. */
export const FUNC_GROUPS: { id: GroupId | 'often' | 'names'; label: string }[] = [
  { id: 'often', label: '자주 쓰는 것' },
  { id: 'sum', label: '합계·통계' },
  { id: 'cond', label: '조건' },
  { id: 'names', label: '이름' },
  { id: 'look', label: '찾기·참조' },
  { id: 'date', label: '날짜·시간' },
  { id: 'math', label: '수학' },
  { id: 'text', label: '글자' },
  { id: 'money', label: '재무' },
  { id: 'tensor', label: '텐서' },
  { id: 'pred', label: '예측·회귀' },
  { id: 'matrix', label: '행렬·선형대수' },
  { id: 'array', label: '배열' },
  { id: 'info', label: '정보' },
  { id: 'lambda', label: 'LET·LAMBDA' },
]

export const OFTEN = ['SUM', 'SUMIFS', 'IF', 'ROUND', 'AVERAGE', 'MAX', 'MIN', 'COUNTIF', 'SUMIF', 'IFERROR', 'XLOOKUP', 'VLOOKUP', 'TODAY', 'EOMONTH', 'TEXT', 'PMT']

const OVER = '[over=차원]'

type Entry = [GroupId, string, string, Record<string, string>?]

const CONDITION = '"식비", ">=10000", "<>0"처럼 따옴표 안에 (* ?로 비슷한 글)'
const KINDS_OF_MATCH = '0: 정확히 같은 것(기본), -1: 같거나 다음으로 작은 것, 1: 같거나 다음으로 큰 것, 2: * ?로 비슷한 글'
const LOAN = {
  이율: '기간마다의 이율: 연 5%를 달마다 내면 5%/12',
  '기간 수': '내는 횟수: 3년을 달마다 내면 36',
  '현재 가치': '지금의 돈: 빌린 돈(받은 돈은 +, 낸 돈은 -)',
  '지급 시점': '0이나 비움: 기간 끝, 1: 기간 처음',
  '미래 가치': '끝에 남길 돈 (비우면 0)',
}
const ORDER = { 차원: '따라갈 순서 차원 (보통 월)' }
const MATRIX = { rows: '행렬의 행으로 볼 차원 이름', cols: '행렬의 열로 볼 차원 이름' }

const CATALOG: Record<string, Entry> = {
  // ---- 합계·통계 ----
  SUM: ['sum', `값1, [값2], …, ${OVER}`, '수를 모두 더합니다. 텐서는 over=로 그 차원을 합칩니다. 예: =SUM(B2:B10), =sum(지출, over=사람)'],
  AVERAGE: ['sum', `값1, [값2], …, ${OVER}`, '평균. 예: =AVERAGE(B2:B10)'],
  MEAN: ['sum', `값1, [값2], …, ${OVER}`, 'AVERAGE와 같습니다(텐서에서 쓰는 이름). 예: =mean(지출, over=월)'],
  MIN: ['sum', `값1, [값2], …, ${OVER}`, '가장 작은 수'],
  MAX: ['sum', `값1, [값2], …, ${OVER}`, '가장 큰 수'],
  COUNT: ['sum', `값1, [값2], …, ${OVER}`, '수가 든 칸의 개수'],
  COUNTA: ['sum', '값1, [값2], …', '비어 있지 않은 칸의 개수'],
  COUNTBLANK: ['sum', '범위', '빈칸의 개수'],
  PRODUCT: ['sum', '값1, [값2], …', '수를 모두 곱합니다'],
  MEDIAN: ['sum', `값1, [값2], …, ${OVER}`, '가운데 값(중앙값)'],
  MODE: ['sum', '값1, [값2], …', '가장 자주 나오는 수'],
  'MODE.SNGL': ['sum', '값1, [값2], …', '가장 자주 나오는 수 (MODE와 같음)'],
  STDEV: ['sum', '값1, [값2], …', '표준편차 (표본, STDEV.S와 같음)'],
  'STDEV.S': ['sum', '값1, [값2], …', '표준편차 (표본)'],
  'STDEV.P': ['sum', '값1, [값2], …', '표준편차 (모두)'],
  STDEVP: ['sum', '값1, [값2], …', '표준편차 (모두, STDEV.P와 같음)'],
  VAR: ['sum', '값1, [값2], …', '분산 (표본, VAR.S와 같음)'],
  'VAR.S': ['sum', '값1, [값2], …', '분산 (표본)'],
  'VAR.P': ['sum', '값1, [값2], …', '분산 (모두)'],
  VARP: ['sum', '값1, [값2], …', '분산 (모두, VAR.P와 같음)'],
  SUMSQ: ['sum', '값1, [값2], …', '제곱해서 더합니다'],
  AVERAGEA: ['sum', '값1, [값2], …', '평균 (글자는 0, TRUE는 1로 셈)'],
  MAXA: ['sum', '값1, [값2], …', '가장 큰 값 (글자는 0, TRUE는 1로 셈)'],
  MINA: ['sum', '값1, [값2], …', '가장 작은 값 (글자는 0, TRUE는 1로 셈)'],
  LARGE: ['sum', '범위, 몇 번째', '몇 번째로 큰 수', { '몇 번째': '1이면 가장 큰 수' }],
  SMALL: ['sum', '범위, 몇 번째', '몇 번째로 작은 수', { '몇 번째': '1이면 가장 작은 수' }],
  PERCENTILE: ['sum', '범위, 비율', '백분위수', { 비율: '0~1 (0.9: 아래에서 90%의 값)' }],
  'PERCENTILE.INC': ['sum', '범위, 비율', '백분위수 (PERCENTILE과 같음)', { 비율: '0~1 (0.9: 아래에서 90%의 값)' }],
  QUARTILE: ['sum', '범위, 사분위', '사분위수', { 사분위: '0: 가장 작은 값, 1, 2: 중앙값, 3, 4: 가장 큰 값' }],
  'QUARTILE.INC': ['sum', '범위, 사분위', '사분위수 (QUARTILE과 같음)', { 사분위: '0: 가장 작은 값, 1, 2: 중앙값, 3, 4: 가장 큰 값' }],
  RANK: ['sum', '수, 범위, [순서]', '범위 안에서 그 수의 순위', { 순서: '0이나 비움: 큰 수가 1등, 1: 작은 수가 1등' }],
  'RANK.EQ': ['sum', '수, 범위, [순서]', '범위 안에서 그 수의 순위 (RANK와 같음)', { 순서: '0이나 비움: 큰 수가 1등, 1: 작은 수가 1등' }],
  SUMPRODUCT: ['sum', '배열1, [배열2], …', '같은 자리끼리 곱해 모두 더합니다. 예: =SUMPRODUCT(B2:B5, C2:C5) (수량 × 가격의 합)'],

  // ---- 조건 ----
  IF: ['cond', '조건, 참일 때, [거짓일 때]', '조건이 맞으면 앞의 값, 아니면 뒤의 값. 예: =IF(B2>100000, "많음", "보통")', { 조건: 'B2>100000, A2="식비"처럼 참·거짓이 되는 식' }],
  IFS: ['cond', '조건1, 값1, [조건2, 값2], …', '처음으로 맞는 조건의 값. 끝에 TRUE, 값을 두면 나머지 모두'],
  IFERROR: ['cond', '값, 오류일 때', '값이 오류(#DIV/0! 등)면 대신 쓸 값. 예: =IFERROR(A2/B2, 0)'],
  IFNA: ['cond', '값, #N/A일 때', '값이 #N/A면 대신 쓸 값'],
  SWITCH: ['cond', '식, 값1, 결과1, [값2, 결과2], …', '식이 값과 같으면 그 결과. 끝에 짝 없이 하나를 두면 어느 것과도 같지 않을 때의 결과'],
  AND: ['cond', '조건1, [조건2], …', '모두 참이면 TRUE'],
  OR: ['cond', '조건1, [조건2], …', '하나라도 참이면 TRUE'],
  XOR: ['cond', '조건1, [조건2], …', '참의 개수가 홀수면 TRUE'],
  NOT: ['cond', '조건', '참과 거짓을 바꿉니다'],
  TRUE: ['cond', '', '참'],
  FALSE: ['cond', '', '거짓'],
  SUMIF: ['cond', '조건 범위, 조건, [합할 범위]', '조건에 맞는 칸만 더합니다. 예: =SUMIF(A2:A100, "식비", B2:B100)', { 조건: CONDITION, '합할 범위': '비우면 조건 범위를 더합니다' }],
  SUMIFS: ['cond', '합할 범위, 조건 범위1, 조건1, [조건 범위2, 조건2], …', '조건을 모두 만족하는 칸만 더합니다. 예: =SUMIFS(C2:C100, A2:A100, "식비", B2:B100, ">=2026-10-01")', { '합할 범위': '더할 수들 (예: C2:C100)', '조건 범위': '조건을 볼 칸들 (합할 범위와 같은 크기)', 조건: CONDITION }],
  COUNTIF: ['cond', '범위, 조건', '조건에 맞는 칸의 개수', { 조건: CONDITION }],
  COUNTIFS: ['cond', '조건 범위1, 조건1, [조건 범위2, 조건2], …', '조건을 모두 만족하는 개수', { 조건: CONDITION }],
  AVERAGEIF: ['cond', '조건 범위, 조건, [평균 범위]', '조건에 맞는 칸의 평균', { 조건: CONDITION }],
  AVERAGEIFS: ['cond', '평균 범위, 조건 범위1, 조건1, [조건 범위2, 조건2], …', '조건을 모두 만족하는 칸의 평균', { 조건: CONDITION }],
  MINIFS: ['cond', '최솟값 범위, 조건 범위1, 조건1, [조건 범위2, 조건2], …', '조건을 모두 만족하는 칸 중 가장 작은 수', { 조건: CONDITION }],
  MAXIFS: ['cond', '최댓값 범위, 조건 범위1, 조건1, [조건 범위2, 조건2], …', '조건을 모두 만족하는 칸 중 가장 큰 수', { 조건: CONDITION }],

  // ---- 수학 ----
  ABS: ['math', '수', '절댓값 (부호를 뗀 수)'],
  INT: ['math', '수', '그 수보다 크지 않은 정수 (-2.5는 -3)'],
  TRUNC: ['math', '수, [자릿수]', '자릿수 아래를 버립니다 (-2.5는 -2)'],
  SIGN: ['math', '수', '부호: 1, 0, -1'],
  SQRT: ['math', '수', '제곱근'],
  ROUND: ['math', '수, 자릿수', '반올림', { 자릿수: '0: 정수로, 2: 소수 둘째 자리까지, -3: 천 단위로' }],
  ROUNDUP: ['math', '수, 자릿수', '올림 (0에서 멀어지게)', { 자릿수: '0: 정수로, 2: 소수 둘째 자리까지, -3: 천 단위로' }],
  ROUNDDOWN: ['math', '수, 자릿수', '내림 (0 쪽으로)', { 자릿수: '0: 정수로, 2: 소수 둘째 자리까지, -3: 천 단위로' }],
  MROUND: ['math', '수, 배수', '가까운 배수로 반올림. 예: =MROUND(B2, 100)'],
  CEILING: ['math', '수, 배수', '배수로 올림. 예: =CEILING(B2, 1000)'],
  FLOOR: ['math', '수, 배수', '배수로 내림'],
  'CEILING.MATH': ['math', '수, [배수], [음수 방향]', '배수로 올림 (배수를 비우면 1)', { '음수 방향': '0이 아니면 음수는 0에서 멀어지게' }],
  'FLOOR.MATH': ['math', '수, [배수], [음수 방향]', '배수로 내림 (배수를 비우면 1)', { '음수 방향': '0이 아니면 음수는 0 쪽으로' }],
  EVEN: ['math', '수', '짝수로 올림'],
  ODD: ['math', '수', '홀수로 올림'],
  MOD: ['math', '수, 나눌 수', '나머지'],
  QUOTIENT: ['math', '수, 나눌 수', '몫 (소수 아래를 버림)'],
  POWER: ['math', '수, 지수', '거듭제곱 (^와 같음)'],
  EXP: ['math', '수', 'e의 거듭제곱'],
  LN: ['math', '수', '자연로그'],
  LOG: ['math', '수, [밑]', '로그 (밑을 비우면 10)'],
  LOG10: ['math', '수', '밑이 10인 로그'],
  FACT: ['math', '수', '계승 (5! = 120)'],
  COMBIN: ['math', '전체, 고를 개수', '조합의 수 (순서 없이 고르기)'],
  PERMUT: ['math', '전체, 고를 개수', '순열의 수 (순서 있게 고르기)'],
  GCD: ['math', '값1, [값2], …', '최대공약수'],
  LCM: ['math', '값1, [값2], …', '최소공배수'],
  PI: ['math', '', '원주율 3.14159…'],
  RAND: ['math', '', '0과 1 사이의 난수 (다시 계산할 때마다 바뀜)'],
  RANDBETWEEN: ['math', '최소, 최대', '그 사이의 정수 난수'],
  SIN: ['math', '각', '사인 (각은 라디안)'],
  COS: ['math', '각', '코사인 (각은 라디안)'],
  TAN: ['math', '각', '탄젠트 (각은 라디안)'],
  ASIN: ['math', '수', '아크사인 (라디안)'],
  ACOS: ['math', '수', '아크코사인 (라디안)'],
  ATAN: ['math', '수', '아크탄젠트 (라디안)'],
  ATAN2: ['math', 'x, y', '점 (x, y)의 각 (라디안)'],
  DEGREES: ['math', '라디안', '도로 바꿉니다'],
  RADIANS: ['math', '도', '라디안으로 바꿉니다'],

  // ---- 찾기·참조 ----
  VLOOKUP: ['look', '찾을 값, 표, 열 번호, [비슷하게]', '표의 첫 열에서 찾아 같은 행의 다른 열 값', { 표: '찾을 열이 맨 왼쪽인 범위 (예: $A$2:$C$100)', '열 번호': '표에서 몇 번째 열의 값을 줄지 (1부터)', 비슷하게: 'FALSE(또는 0): 정확히 같은 것만 (보통 이것), TRUE: 정렬된 표에서 같거나 작은 것' }],
  HLOOKUP: ['look', '찾을 값, 표, 행 번호, [비슷하게]', '표의 첫 행에서 찾아 같은 열의 다른 행 값', { '행 번호': '표에서 몇 번째 행의 값을 줄지 (1부터)', 비슷하게: 'FALSE(또는 0): 정확히 같은 것만, TRUE: 비슷한 것' }],
  XLOOKUP: ['look', '찾을 값, 찾을 범위, 돌려줄 범위, [없을 때], [맞추는 방법], [찾는 방향]', '찾을 범위에서 찾아 돌려줄 범위의 같은 자리 값. 예: =XLOOKUP("식비", A2:A20, B2:B20, 0)', { '돌려줄 범위': '찾을 범위와 같은 크기', '없을 때': '찾지 못했을 때의 값 (비우면 #N/A)', '맞추는 방법': KINDS_OF_MATCH, '찾는 방향': '1: 처음부터(기본), -1: 끝에서부터' }],
  MATCH: ['look', '찾을 값, 범위, [맞추는 방법]', '범위에서 몇 번째에 있는지', { '맞추는 방법': '0: 정확히, 1(기본): 오름차순에서 같거나 작은 것, -1: 내림차순에서 같거나 큰 것' }],
  XMATCH: ['look', '찾을 값, 범위, [맞추는 방법], [찾는 방향]', '범위에서 몇 번째에 있는지 (XLOOKUP과 같은 방법)', { '맞추는 방법': KINDS_OF_MATCH, '찾는 방향': '1: 처음부터(기본), -1: 끝에서부터' }],
  INDEX: ['look', '범위, 행 번호, [열 번호], [영역 번호]', '범위에서 그 행·열의 값. 예: =INDEX(B2:D10, 3, 2)'],
  CHOOSE: ['look', '번호, 값1, [값2], …', '번호째 값을 고릅니다'],
  OFFSET: ['look', '기준, 행 이동, 열 이동, [높이], [너비]', '기준 칸에서 옮긴 범위'],
  INDIRECT: ['look', '주소 글, [A1 방식]', '글로 쓴 주소의 칸. 예: =INDIRECT("B" & A1)'],
  ROW: ['look', '[칸]', '행 번호 (비우면 이 칸)'],
  COLUMN: ['look', '[칸]', '열 번호 (비우면 이 칸)'],
  ROWS: ['look', '범위', '행의 수'],
  COLUMNS: ['look', '범위', '열의 수'],

  // ---- 날짜·시간 ----
  TODAY: ['date', '', '오늘 날짜 (한국 시간)'],
  NOW: ['date', '', '지금 날짜와 시각'],
  DATE: ['date', '연, 월, 일', '날짜를 만듭니다. 예: =DATE(2026, 10, 1)'],
  YEAR: ['date', '날짜', '연도'],
  MONTH: ['date', '날짜', '월 (1~12)'],
  DAY: ['date', '날짜', '일 (1~31)'],
  YM: ['date', '날짜', '날짜의 "YYYY-MM" (텐서의 월). 예: ym(거래.날짜)'],
  EOMONTH: ['date', '날짜, 개월 수', '몇 달 뒤 그 달의 마지막 날', { '개월 수': '0: 이번 달 말일, 1: 다음 달 말일, -1: 지난달 말일' }],
  EDATE: ['date', '날짜, 개월 수', '몇 달 뒤의 같은 날', { '개월 수': '음수면 앞으로' }],
  WEEKDAY: ['date', '날짜, [방식]', '요일 번호', { 방식: '1이나 비움: 일요일 1 ~ 토요일 7, 2: 월요일 1 ~ 일요일 7, 3: 월요일 0 ~ 일요일 6' }],
  WEEKNUM: ['date', '날짜, [방식]', '그 해의 몇째 주', { 방식: '1이나 비움: 일요일에 시작하는 주, 2: 월요일에 시작하는 주' }],
  ISOWEEKNUM: ['date', '날짜', 'ISO 주 번호 (월요일에 시작)'],
  DAYS: ['date', '끝 날짜, 시작 날짜', '두 날짜 사이의 날 수'],
  DATEDIF: ['date', '시작 날짜, 끝 날짜, 단위', '두 날짜 사이의 기간', { 단위: '"Y": 해, "M": 달, "D": 날, "YM": 해를 뺀 달, "MD": 달을 뺀 날, "YD": 해를 뺀 날' }],
  NETWORKDAYS: ['date', '시작 날짜, 끝 날짜, [쉬는 날]', '주말을 뺀 일하는 날 수', { '쉬는 날': '공휴일 날짜들의 범위' }],
  'NETWORKDAYS.INTL': ['date', '시작 날짜, 끝 날짜, [주말], [쉬는 날]', '정한 주말을 뺀 일하는 날 수', { 주말: '1이나 비움: 토·일, 11: 일요일만, "0000011": 월~일 중 쉬는 날을 1로' }],
  WORKDAY: ['date', '시작 날짜, 일하는 날 수, [쉬는 날]', '주말을 빼고 세어 며칠 뒤', { '쉬는 날': '공휴일 날짜들의 범위' }],
  'WORKDAY.INTL': ['date', '시작 날짜, 일하는 날 수, [주말], [쉬는 날]', '정한 주말을 빼고 세어 며칠 뒤', { 주말: '1이나 비움: 토·일, 11: 일요일만, "0000011": 월~일 중 쉬는 날을 1로' }],
  YEARFRAC: ['date', '시작 날짜, 끝 날짜, [기준]', '두 날짜 사이를 해로 (1.5년 등)'],
  DATEVALUE: ['date', '날짜 글', '글로 쓴 날짜를 날짜 값으로. 예: =DATEVALUE("2026-10-01")'],
  TIME: ['date', '시, 분, 초', '시각을 만듭니다'],
  TIMEVALUE: ['date', '시각 글', '글로 쓴 시각을 시각 값으로'],
  HOUR: ['date', '시각', '시 (0~23)'],
  MINUTE: ['date', '시각', '분'],
  SECOND: ['date', '시각', '초'],

  // ---- 글자 ----
  LEN: ['text', '글', '글자 수'],
  LEFT: ['text', '글, [글자 수]', '앞에서 몇 글자 (비우면 1)'],
  RIGHT: ['text', '글, [글자 수]', '뒤에서 몇 글자 (비우면 1)'],
  MID: ['text', '글, 시작 위치, 글자 수', '가운데 몇 글자'],
  FIND: ['text', '찾을 글, 글, [시작 위치]', '찾을 글의 위치 (대소문자 구분)'],
  SEARCH: ['text', '찾을 글, 글, [시작 위치]', '찾을 글의 위치 (대소문자 무시, * ? 가능)'],
  SUBSTITUTE: ['text', '글, 찾을 글, 바꿀 글, [몇 번째]', '찾을 글을 바꿀 글로 (몇 번째를 비우면 모두)'],
  REPLACE: ['text', '글, 시작 위치, 글자 수, 새 글', '그 자리의 글자를 새 글로'],
  REPT: ['text', '글, 횟수', '글을 되풀이합니다'],
  UPPER: ['text', '글', '영문을 대문자로'],
  LOWER: ['text', '글', '영문을 소문자로'],
  PROPER: ['text', '글', '영문 단어의 첫 글자만 대문자로'],
  TRIM: ['text', '글', '앞뒤 빈칸과 겹친 빈칸을 지웁니다'],
  CLEAN: ['text', '글', '인쇄되지 않는 글자를 지웁니다'],
  CONCAT: ['text', '글1, [글2], …', '이어 붙입니다 (범위도 됨). & 와 같음'],
  CONCATENATE: ['text', '글1, [글2], …', '이어 붙입니다'],
  TEXTJOIN: ['text', '구분자, 빈칸 무시, 글1, [글2], …', '사이에 구분자를 넣어 이어 붙입니다. 예: =TEXTJOIN(", ", TRUE, A2:A10)', { '빈칸 무시': 'TRUE: 빈칸은 건너뜀' }],
  TEXT: ['text', '값, 형식', '형식에 맞춘 글로. 예: =TEXT(B2, "#,##0원")', { 형식: '"#,##0", "0.0%", "yyyy-mm-dd", "m월 d일"처럼 따옴표 안에' }],
  TEXTBEFORE: ['text', '글, 구분자, [몇 번째], [대소문자 무시], [끝 맞춤], [없을 때]', '구분자 앞의 글'],
  TEXTAFTER: ['text', '글, 구분자, [몇 번째], [대소문자 무시], [끝 맞춤], [없을 때]', '구분자 뒤의 글'],
  TEXTSPLIT: ['text', '글, 열 구분자, [행 구분자], [빈칸 무시], [대소문자 무시], [채울 값]', '구분자로 나누어 배열로'],
  VALUE: ['text', '글', '글을 수로'],
  NUMBERVALUE: ['text', '글, [소수점 기호], [자릿수 기호]', '글을 수로 (기호를 정해서)'],
  FIXED: ['text', '수, [자릿수], [쉼표 없이]', '자릿수에 맞춘 글 (천 단위 쉼표)'],
  EXACT: ['text', '글1, 글2', '똑같으면 TRUE (대소문자 구분)'],
  CHAR: ['text', '번호', '그 번호의 글자'],
  UNICHAR: ['text', '번호', '그 유니코드 번호의 글자'],
  CODE: ['text', '글', '첫 글자의 번호'],
  UNICODE: ['text', '글', '첫 글자의 유니코드 번호'],
  T: ['text', '값', '글이면 그대로, 아니면 빈 글'],
  N: ['text', '값', '수면 그대로, 글은 0'],

  // ---- 재무 ----
  PMT: ['money', '이율, 기간 수, 현재 가치, [미래 가치], [지급 시점]', '대출·적금에서 매기 내는 돈. 예: =PMT(5%/12, 36, 10000000)', LOAN],
  IPMT: ['money', '이율, 회차, 기간 수, 현재 가치, [미래 가치], [지급 시점]', '그 회차에 내는 이자', { ...LOAN, 회차: '몇 번째 낼 때 (1부터)' }],
  PPMT: ['money', '이율, 회차, 기간 수, 현재 가치, [미래 가치], [지급 시점]', '그 회차에 갚는 원금', { ...LOAN, 회차: '몇 번째 낼 때 (1부터)' }],
  FV: ['money', '이율, 기간 수, 매기 내는 돈, [현재 가치], [지급 시점]', '미래 가치 (적금 만기에 받을 돈 등)', { ...LOAN, '매기 내는 돈': '내는 돈은 - (예: -300000)' }],
  PV: ['money', '이율, 기간 수, 매기 내는 돈, [미래 가치], [지급 시점]', '현재 가치', LOAN],
  NPER: ['money', '이율, 매기 내는 돈, 현재 가치, [미래 가치], [지급 시점]', '몇 번 내야 하는지 (기간 수)', LOAN],
  RATE: ['money', '기간 수, 매기 내는 돈, 현재 가치, [미래 가치], [지급 시점], [추정값]', '기간마다의 이율 (연 이율은 ×12)', LOAN],
  CUMIPMT: ['money', '이율, 기간 수, 현재 가치, 시작 회차, 끝 회차, 지급 시점', '그 회차들에 낸 이자의 합', LOAN],
  CUMPRINC: ['money', '이율, 기간 수, 현재 가치, 시작 회차, 끝 회차, 지급 시점', '그 회차들에 갚은 원금의 합', LOAN],
  EFFECT: ['money', '명목 연이율, 한 해 복리 횟수', '실효 연이율'],
  NOMINAL: ['money', '실효 연이율, 한 해 복리 횟수', '명목 연이율'],
  NPV: ['money', '할인율, 현금 흐름1, [현금 흐름2], …', '순현재가치 (기간 끝마다 들어오고 나가는 돈)'],
  IRR: ['money', '현금 흐름, [추정값]', '내부 수익률 (처음에 낸 돈은 -)'],

  // ---- 텐서 ----
  TOTAL: ['tensor', '값, by=키', '거래 표를 키마다 묶어 더한 텐서. 예: TOTAL(거래.금액, by=(거래.사람, 월=ym(거래.날짜)))'],
  SHIFT: ['tensor', '값, 차원, [칸 수]', '그 차원을 따라 칸 수만큼 앞의 값 (지난달 등, 비우면 1). 예: shift(x, 월, 1)', ORDER],
  CUMSUM: ['tensor', '값, 차원', '그 차원을 따라 쌓은 누계', ORDER],
  DIFF: ['tensor', '값, 차원', '그 차원을 따라 앞 값과의 차이', ORDER],

  // ---- 예측·회귀 ----
  PREDTREND: ['pred', '값, [along=차원], [horizon=개수]', '추세(직선)로 다음 값을 예측합니다. 예: =SUM(predtrend(지출[분류=식비], along=월)) (다음 달 식비), =predtrend(B2:B13, horizon=3)'],
  TREND: ['pred', '알려진 y, [알려진 x], [새 x], [상수 포함]', '직선 추세의 값'],
  GROWTH: ['pred', '알려진 y, [알려진 x], [새 x], [상수 포함]', '지수 추세의 값'],
  FORECAST: ['pred', 'x, 알려진 y, 알려진 x', '직선으로 x에서의 y를 예측'],
  'FORECAST.LINEAR': ['pred', 'x, 알려진 y, 알려진 x', '직선으로 x에서의 y를 예측 (FORECAST와 같음)'],
  LINEST: ['pred', '알려진 y, [알려진 x], [상수 포함], [통계]', '직선 회귀의 기울기와 절편'],
  LOGEST: ['pred', '알려진 y, [알려진 x], [상수 포함], [통계]', '지수 회귀'],
  SLOPE: ['pred', '알려진 y, 알려진 x', '회귀 직선의 기울기'],
  INTERCEPT: ['pred', '알려진 y, 알려진 x', '회귀 직선의 절편'],
  RSQ: ['pred', '알려진 y, 알려진 x', '결정계수 (R²)'],
  CORREL: ['pred', '배열1, 배열2', '상관계수'],
  PEARSON: ['pred', '배열1, 배열2', '상관계수 (CORREL과 같음)'],
  STEYX: ['pred', '알려진 y, 알려진 x', '회귀의 표준오차'],
  COVAR: ['pred', '배열1, 배열2', '공분산 (모두)'],
  'COVARIANCE.P': ['pred', '배열1, 배열2', '공분산 (모두)'],
  'COVARIANCE.S': ['pred', '배열1, 배열2', '공분산 (표본)'],

  // ---- 행렬·선형대수 ----
  MMULT: ['matrix', '배열1, 배열2', '행렬 곱'],
  MINVERSE: ['matrix', '배열', '역행렬'],
  MDETERM: ['matrix', '배열', '행렬식'],
  MUNIT: ['matrix', '크기', '단위 행렬'],
  TRANSPOSE: ['matrix', '배열', '행과 열을 바꿉니다'],
  CONTRACT: ['matrix', 'a, b, over=차원', '같은 차원끼리 곱해 그 차원으로 더합니다 (텐서 곱). 예: =contract(수량, 가격, over=품목)'],
  SOLVE: ['matrix', 'A, b, rows=차원, cols=차원', 'A x = b를 풉니다', MATRIX],
  INVERSE: ['matrix', 'A, rows=차원, cols=차원', '역행렬 (결과는 [cols, rows])', MATRIX],
  DET: ['matrix', 'A, rows=차원, cols=차원', '행렬식', MATRIX],
  LSTSQ: ['matrix', 'X, y, [rows=차원], [cols=차원]', '최소제곱 해 (회귀 계수)', MATRIX],
  SVD: ['matrix', 'A, rows=차원, cols=차원, [part="s"]', '특잇값 분해 (새 차원: 성분)', { ...MATRIX, part: '"s": 특잇값(기본), "u", "v"' }],
  EIG: ['matrix', 'A, rows=차원, cols=차원, [part="values"]', '대칭 행렬의 고윳값·고유벡터 (새 차원: 성분)', { ...MATRIX, part: '"values": 고윳값(기본), "vectors": 고유벡터' }],
  QR: ['matrix', 'A, rows=차원, cols=차원, part="q"', 'QR 분해 (새 차원: 성분)', { ...MATRIX, part: '"q" 또는 "r"' }],
  CHOLESKY: ['matrix', 'A, rows=차원, cols=차원', '촐레스키 분해 (대칭이고 양의 정부호)', MATRIX],

  // ---- 배열 ----
  FILTER: ['array', '배열, 조건, [없을 때]', '조건에 맞는 행만. 예: =FILTER(A2:C100, B2:B100="식비")', { 조건: '배열과 같은 길이의 참·거짓 (예: B2:B100="식비")' }],
  SORT: ['array', '배열, [기준 열], [순서], [열로]', '정렬합니다', { 순서: '1: 작은 것부터(기본), -1: 큰 것부터' }],
  SORTBY: ['array', '배열, 기준1, [순서1], [기준2, 순서2], …', '다른 배열을 기준으로 정렬', { 순서: '1: 작은 것부터(기본), -1: 큰 것부터' }],
  UNIQUE: ['array', '배열, [열로], [한 번만]', '겹치지 않는 값'],
  SEQUENCE: ['array', '행 수, [열 수], [시작], [간격]', '차례로 수를 만듭니다. 예: =SEQUENCE(12) (1~12)'],
  TAKE: ['array', '배열, 행 수, [열 수]', '앞에서(음수면 뒤에서) 몇 행·열'],
  DROP: ['array', '배열, 행 수, [열 수]', '앞(음수면 뒤)의 몇 행·열을 뺀 나머지'],
  EXPAND: ['array', '배열, 행 수, [열 수], [채울 값]', '크기를 늘립니다'],
  VSTACK: ['array', '배열1, [배열2], …', '위아래로 붙입니다'],
  HSTACK: ['array', '배열1, [배열2], …', '옆으로 붙입니다'],
  TOCOL: ['array', '배열, [무시], [열 순서로]', '한 열로 폅니다'],
  TOROW: ['array', '배열, [무시], [열 순서로]', '한 행으로 폅니다'],
  CHOOSECOLS: ['array', '배열, 열 번호1, [열 번호2], …', '고른 열만'],
  CHOOSEROWS: ['array', '배열, 행 번호1, [행 번호2], …', '고른 행만'],
  WRAPROWS: ['array', '줄, 개수, [채울 값]', '몇 개씩 끊어 행으로'],
  WRAPCOLS: ['array', '줄, 개수, [채울 값]', '몇 개씩 끊어 열로'],

  // ---- 정보 ----
  ISNUMBER: ['info', '값', '수면 TRUE'],
  ISTEXT: ['info', '값', '글이면 TRUE'],
  ISNONTEXT: ['info', '값', '글이 아니면 TRUE'],
  ISBLANK: ['info', '값', '빈칸이면 TRUE'],
  ISLOGICAL: ['info', '값', 'TRUE나 FALSE면 TRUE'],
  ISERROR: ['info', '값', '오류면 TRUE'],
  ISERR: ['info', '값', '#N/A가 아닌 오류면 TRUE'],
  ISNA: ['info', '값', '#N/A면 TRUE'],
  ISEVEN: ['info', '수', '짝수면 TRUE'],
  ISODD: ['info', '수', '홀수면 TRUE'],
  ISREF: ['info', '값', '칸 참조면 TRUE'],
  ISFORMULA: ['info', '칸', '수식이 든 칸이면 TRUE'],
  'ERROR.TYPE': ['info', '오류', '오류의 번호 (#DIV/0!은 2 등)'],
  TYPE: ['info', '값', '값의 종류: 1 수, 2 글, 4 참·거짓, 16 오류, 64 배열'],
  NA: ['info', '', '#N/A (값 없음)를 만듭니다'],

  // ---- LET·LAMBDA ----
  LET: ['lambda', '이름1, 값1, [이름2, 값2], …, 계산', '값에 이름을 붙이고 마지막 계산에서 씁니다. 예: =LET(합, SUM(B2:B10), 합 * 1.1)'],
  LAMBDA: ['lambda', '[이름1], …, 계산', '함수를 만듭니다. 예: =MAP(B2:B5, LAMBDA(v, v * 2))'],
  MAP: ['lambda', '배열1, [배열2], …, 함수', '배열의 값마다 함수를 부릅니다'],
  BYROW: ['lambda', '배열, 함수', '행마다 함수를 부릅니다'],
  BYCOL: ['lambda', '배열, 함수', '열마다 함수를 부릅니다'],
  REDUCE: ['lambda', '처음 값, 배열, 함수', '함수로 배열을 값 하나로 (처음 값은 비워도 됨). 함수: LAMBDA(누계, 값, …)'],
  SCAN: ['lambda', '처음 값, 배열, 함수', 'REDUCE의 중간값을 모두'],
  MAKEARRAY: ['lambda', '행 수, 열 수, 함수', '함수(LAMBDA(행, 열, …))로 배열을 만듭니다'],
}

/** Hints of the named arguments where a function has none of its own. */
const NAMED_HINT: Record<string, string> = {
  over: '합쳐 없앨 차원 (여럿이면 over=(사람, 분류))',
  by: '묶을 키: by=(거래.사람, 월=ym(거래.날짜))',
  along: '따라갈 순서 차원 (보통 월, 표의 칸이면 비움)',
  horizon: '몇 개 앞까지 (비우면 1)',
  rows: '행렬의 행으로 볼 차원 이름',
  cols: '행렬의 열로 볼 차원 이름',
  part: '돌려받을 결과',
}

export interface Arg {
  /** as shown: "조건 범위1", "over=차원" */
  name: string
  optional: boolean
  /** over=, by=, along=, horizon=, rows=, cols=, part= */
  named: string | null
}

export interface FuncInfo {
  name: string
  group: GroupId
  desc: string
  args: Arg[]
  /** the positional arguments before "…" ([조건 범위2, 조건2] repeats), and those after it */
  head: Arg[]
  repeat: number
  tail: Arg[]
  hints: Record<string, string>
}

function argOf(text: string, optional: boolean): Arg {
  const m = /^([a-z]+)=/.exec(text)
  return { name: text, optional, named: m ? m[1]! : null }
}

/** Parses a signature ("합할 범위, 조건 범위1, 조건1, [조건 범위2, 조건2], …"). */
export function parseSig(sig: string): Pick<FuncInfo, 'args' | 'head' | 'repeat' | 'tail'> {
  const args: Arg[] = []
  const head: Arg[] = []
  const tail: Arg[] = []
  let repeat = 0
  let dots = false
  let lastGroup = 0
  for (const part of sig.match(/\[[^\]]*\]|[^,[\]]+/g) ?? []) {
    const t = part.trim()
    if (t === '') continue
    if (t === '…') {
      dots = true
      repeat = lastGroup
      continue
    }
    const optional = t.startsWith('[')
    const items = (optional ? t.slice(1, -1) : t).split(',').map((x) => x.trim())
    const group = items.map((x) => argOf(x, optional))
    args.push(...group)
    const positional = group.filter((a) => a.named === null)
    if (positional.length === 0) continue
    if (dots) tail.push(...positional)
    else {
      head.push(...positional)
      lastGroup = positional.length
    }
  }
  return { args, head, repeat: dots ? repeat : 0, tail }
}

const INFO = new Map<string, FuncInfo>()
for (const [name, [group, sig, desc, hints]] of Object.entries(CATALOG))
  INFO.set(name, { name, group, desc, hints: hints ?? {}, ...parseSig(sig) })

/** Every function's name (the engine's), in the catalog's order. */
export const FUNC_NAMES: readonly string[] = [...INFO.keys()]

/** A function by name (any case), or undefined. */
export function funcInfo(name: string): FuncInfo | undefined {
  return INFO.get(name.toUpperCase())
}

/** A category's functions (often: the often used ones, in their order). */
export function funcsIn(group: GroupId | 'often'): FuncInfo[] {
  if (group === 'often') return OFTEN.map((n) => INFO.get(n)!)
  return [...INFO.values()].filter((f) => f.group === group)
}

/** "SUMIFS(합할 범위, 조건 범위1, 조건1, [조건 범위2, 조건2], …)" */
export function signature(f: FuncInfo): string {
  return `${f.name}(${CATALOG[f.name]![1]})`
}

/** Functions whose names start with prefix (any case): the often used first. */
export function funcsStarting(prefix: string): FuncInfo[] {
  const p = prefix.toUpperCase()
  if (p === '') return []
  const all = [...INFO.values()].filter((f) => f.name.startsWith(p))
  const rank = (f: FuncInfo) => {
    const i = OFTEN.indexOf(f.name)
    return i < 0 ? OFTEN.length : i
  }
  return all.sort((a, b) => rank(a) - rank(b) || a.name.length - b.name.length || a.name.localeCompare(b.name))
}

// ---- where the caret is in a formula ----

export interface CallAt {
  name: string
  /** the positional argument being typed (0 first; named ones do not count) */
  index: number
  /** a named argument being typed ("over"), or null */
  named: string | null
}

interface Frame {
  /** the function's name (upper case) for a call; null for a (group), [slice] or {array} */
  name: string | null
  open: string
  /** arguments before the current one, and how many of them were named */
  index: number
  named: number
  argStart: number
}

/** "over" for "over=사람" (not "over==1"), else null. */
function keywordOf(arg: string): string | null {
  const m = /^\s*([A-Za-z]+)\s*=(?!=)/.exec(arg)
  const k = m ? m[1]!.toLowerCase() : null
  return k !== null && NAMED_HINT[k] !== undefined ? k : null
}

/** The open calls and brackets at the caret, or quoted: in a "string" or 'sheet name'. */
function scan(text: string, caret: number): { frames: Frame[]; quoted: boolean } {
  const frames: Frame[] = []
  let i = text.startsWith('=') ? 1 : 0
  while (i < caret) {
    const ch = text[i]!
    if (ch === '"' || ch === "'") {
      // to the closing quote ("" or '' inside is one)
      let j = i + 1
      for (;;) {
        if (j >= caret) return { frames, quoted: true }
        if (text[j] === ch) {
          if (j + 1 < caret && text[j + 1] === ch) {
            j += 2
            continue
          }
          break
        }
        j++
      }
      i = j + 1
      continue
    }
    if (ch === '(') {
      const m = /([A-Za-z_][A-Za-z0-9_.]*)\s*$/.exec(text.slice(0, i))
      frames.push({ name: m ? m[1]!.toUpperCase() : null, open: '(', index: 0, named: 0, argStart: i + 1 })
    } else if (ch === '[' || ch === '{') frames.push({ name: null, open: ch, index: 0, named: 0, argStart: i + 1 })
    else if (ch === ')' || ch === ']' || ch === '}') frames.pop()
    else if (ch === ',' || ch === ';') {
      const top = frames[frames.length - 1]
      if (top) {
        if (top.open === '(' && keywordOf(text.slice(top.argStart, i)) !== null) top.named++
        top.index++
        top.argStart = i + 1
      }
    }
    i++
  }
  return { frames, quoted: false }
}

/**
 * The call the caret is in: its name and which argument is being typed, or
 * null (not a formula, in a string, or in no call). In a [slice] or an
 * inner (group), the call around it.
 */
export function callAt(text: string, caret: number): CallAt | null {
  if (!text.startsWith('=')) return null
  const s = scan(text, caret)
  if (s.quoted) return null
  for (let k = s.frames.length - 1; k >= 0; k--) {
    const f = s.frames[k]!
    if (f.open !== '(' || f.name === null) continue
    return { name: f.name, index: f.index - f.named, named: keywordOf(text.slice(f.argStart, caret)) }
  }
  return null
}

/** The name being typed at the caret in a formula ("SU", "지"), and where it starts. */
export function tokenAt(text: string, caret: number): { from: number; text: string } | null {
  if (!text.startsWith('=')) return null
  if (scan(text, caret).quoted) return null
  const m = /[\p{L}_][\p{L}\p{N}_.]*$/u.exec(text.slice(0, caret))
  if (!m) return null
  const from = caret - m[0].length
  // a part of something else ($A1, Sheet1!B2, 1E5)
  if (/[\p{L}\p{N}_.$!]/u.test(text[from - 1] ?? '')) return null
  // in the middle of a name
  if (/[\p{L}\p{N}_.(]/u.test(text[caret] ?? '')) return null
  return { from, text: m[0] }
}

export interface GuidePart {
  text: string
  current: boolean
}

/**
 * The guide for the argument being typed: the signature in parts (the
 * current argument marked), its name as counted ("조건 범위3" for a third
 * pair) and what to put there (else what the function does).
 */
export function argGuide(f: FuncInfo, index: number, named: string | null): { parts: GuidePart[]; label: string; hint: string } {
  let current: Arg | null = null
  let label = ''
  if (named !== null) current = f.args.find((a) => a.named === named) ?? null
  else if (index < f.head.length) current = f.head[index]!
  else if (f.repeat > 0) {
    const start = f.head.length - f.repeat
    const k = (index - start) % f.repeat
    current = f.head[start + k]!
    const round = Math.floor((index - start) / f.repeat)
    const m = /^(.*?)(\d+)$/.exec(current.name)
    label = m ? `${m[1]}${Number(m[2]) + round}` : current.name
  }
  if (current !== null && label === '') label = current.name
  const parts: GuidePart[] = [{ text: `${f.name}(`, current: false }]
  for (const piece of CATALOG[f.name]![1].split(/(\[|\]|, |…)/)) {
    if (piece === '') continue
    const a = f.args.find((x) => x.name === piece)
    parts.push({ text: piece, current: current !== null && a === current })
  }
  parts.push({ text: ')', current: false })
  if (current === null) return { parts, label, hint: f.desc }
  const own = f.hints[current.named ?? current.name] ?? f.hints[current.name.replace(/\d+$/, '')]
  const hint = own ?? (current.named !== null ? NAMED_HINT[current.named]! : '')
  return { parts, label, hint }
}
